// PostHog — 앱·웹·icucompany 공통 행동 분석(한 프로젝트, `site` 로 구분).
//
// 식별 규칙은 앱(korehalal_app PosthogConfig.identityFor)과 같다: 회원만, 서버 uuid 로.
// 게스트를 identify 하면 게스트 발급마다 person 이 생겨 회원 비율이 부푼다.
//
// ⚠️ 예약 uuid 는 절대 보내지 않는다 — analytics.ts 상단 경고와 같은 이유(bearer capability).
import posthog from "posthog-js";
import type { UserData } from "../models/user.model.ts";

let enabled = false;

// bearer 가 되는 경로 세그먼트: 예약 상세·배달 주문 상세. 쿼리·해시(인증 콜백 포함)는 통째로 버린다.
const MASKED = /(\/(?:reservation|booking|delivery\/orders)\/)[^/?#]+/;

export function sanitizeUrl(url: string): string {
  return url.replace(/[?#].*$/, "").replace(MASKED, "$1:uuid");
}

// posthog-js 가 원본 주소를 싣는 키는 계속 늘어난다($prev_pageview_pathname, $session_entry_* …) — 이름 패턴으로 잡는다.
const URL_KEY = /(url|pathname|referrer)$/i;

function scrub(bag: any): void {
  if (!bag) return;
  for (const k of Object.keys(bag)) if (URL_KEY.test(k) && typeof bag[k] === "string") bag[k] = sanitizeUrl(bag[k]);
}

function beforeSend(event: any) {
  if (event) {
    scrub(event.properties);
    scrub(event.$set);
    scrub(event.$set_once);
  }
  return event;
}

// 운영 호스트에서만 켠다 — 키가 로컬 .env.production 으로 새도 deploy:dev·stage 빌드는 no-op.
const PROD_HOSTS = ["korehalal.com", "www.korehalal.com"];

export function initPosthog(): void {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key || !PROD_HOSTS.includes(window.location.hostname)) return;
  posthog.init(key, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: "history_change",
    autocapture: false, // 요소 href 가 URL 을 실어 나른다
    disable_session_recording: true,
    // 웹 바이탈·히트맵·데드클릭은 중첩 속성/객체 키에 원본 URL 을 싣는다 — before_send 가 못 가리므로 프로젝트 토글과 무관하게 끈다
    capture_performance: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    before_send: beforeSend,
  });
  posthog.register({ site: "web", environment: "production" });
  enabled = true;
}

export function identifyUser(user: UserData | null): void {
  if (!enabled || !user?.uuid || (user.type as string) === "GUEST") return;
  posthog.identify(user.uuid, {
    uuid: user.uuid,
    ...(user.country_code ? { country_code: user.country_code } : {}),
    ...(user.created_at ? { signup_at: user.created_at } : {}),
  });
}

export function resetUser(): void {
  if (!enabled) return;
  posthog.reset();
  // reset 이 등록 속성을 지운다 — 로그아웃 뒤 이벤트도 site·environment 를 달아야 한다
  posthog.register({ site: "web", environment: "production" });
}

export function capture(event: string, props: Record<string, unknown> = {}): void {
  if (enabled) posthog.capture(event, props);
}
